import { useState } from "react";

/**
 * Slices already-in-memory rows into pages for a `Table` + `TablePagination`
 * pair, and returns to the first page whenever the row set is re-cut.
 *
 * The reset is derived rather than run from an effect, so a narrowed result
 * set never paints one frame of the old page before snapping back.
 *
 * `resetOn` must hold primitives, not the objects they came from. Filter state
 * backed by URL params hands back a fresh object identity on every param
 * write — including writes made by an unrelated control elsewhere on the page —
 * so depending on the object would bounce this table to page 1 whenever a
 * neighbouring one was filtered.
 */
export function usePagedRows<T>({
  rows,
  pageSize,
  resetOn,
}: {
  rows: T[];
  pageSize: number;
  resetOn: (string | number | boolean | null | undefined)[];
}): { page: number; pageRows: T[]; setPage: (page: number) => void } {
  // A separator no filter value can contain, so distinct inputs can't collide
  // into one key and suppress a reset.
  const resetKey = resetOn.join(" ");
  const [pageState, setPageState] = useState({ key: resetKey, page: 0 });

  const totalPages = Math.ceil(rows.length / pageSize);
  const requestedPage = pageState.key === resetKey ? pageState.page : 0;
  // Clamped so a page index left over from a larger result set can never
  // address past the end.
  const page = Math.min(requestedPage, Math.max(totalPages - 1, 0));
  // The clamp is stored, not just applied, so rows that shrink and later grow
  // back stay on the page the user was shown rather than jumping to one that
  // had disappeared.
  if (page !== requestedPage) {
    setPageState({ key: resetKey, page });
  }

  return {
    page,
    pageRows: rows.slice(page * pageSize, (page + 1) * pageSize),
    setPage: (next: number) => setPageState({ key: resetKey, page: next }),
  };
}
